import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  updateSOAP,
  patchCopyDone,
  patchMarkDismissed,
  patchUndoDismissed,
  detachNSIPS,
  patchSessionType,
  patchGenerateFromMemo,
  patchManualSoap,
} from '@/shared/apis/yakureki'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { SessionType } from '@/shared/types/yakurekiCore'
import { SoapContent } from '@/shared/types/yakurekiSoap'

// regenerate 系は app 階層に物理分離 (= shared から regenerate 系の依存を切る):
//   - 旧 path (音声 reprocess / legacy): apps/legacy/features/yakureki/hooks/useRegenerateFromAudioMutation
//   - 新 path (dictated → SOAP only / latest): apps/latest/features/yakureki/hooks/useRegenerateFromDictatedMutation

// ========================================
// Types
// ========================================

interface UpdateSoapVariables {
  yakurekiId: string
  soapData: SoapContent
  editSource?: string
}

// ========================================
// useUpdateSoapMutation
// ========================================

/**
 * SOAP更新Mutation（TanStack Query）
 *
 * 責務:
 * - SOAP内容の更新
 * - 成功時にyakurekiキャッシュをinvalidate
 *
 * 使用例:
 * ```tsx
 * const updateSoap = useUpdateSoapMutation()
 * await updateSoap.mutateAsync({
 *   yakurekiId: 'xxx',
 *   soapData: { subjectives: [...] },
 * })
 * ```
 */
export const useUpdateSoapMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      yakurekiId,
      soapData,
      editSource,
    }: UpdateSoapVariables) => {
      await updateSOAP(yakurekiId, soapData, editSource)
    },
    onSuccess: (_, variables) => {
      // 該当薬歴のキャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(variables.yakurekiId),
      })
      // 編集履歴のキャッシュもinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.soapEditHistory.list(variables.yakurekiId),
      })
    },
  })
}

// ========================================
// useUpdateSoapSelectionMutation
// ========================================

/**
 * SOAP選択状態更新Mutation（TanStack Query）
 *
 * 責務:
 * - SOAP選択状態（is_selected）のサーバー同期
 * - 軽量な更新のためキャッシュinvalidateはしない
 *
 * 使用例:
 * ```tsx
 * const updateSelection = useUpdateSoapSelectionMutation()
 * await updateSelection.mutateAsync({
 *   yakurekiId: 'xxx',
 *   soapData: { subjectives: [...], ... },
 * })
 * ```
 */
export const useUpdateSoapSelectionMutation = () => {
  return useMutation({
    mutationFn: async ({ yakurekiId, soapData }: UpdateSoapVariables) => {
      await updateSOAP(yakurekiId, soapData)
    },
    // 選択状態の同期は軽量処理なのでキャッシュinvalidateしない
  })
}

// ========================================
// usePatchCopyDoneMutation
// ========================================

/**
 * コピー完了マークMutation（TanStack Query）
 *
 * 責務:
 * - コピー済みフラグの更新
 *
 * 使用例:
 * ```tsx
 * const patchCopy = usePatchCopyDoneMutation()
 * await patchCopy.mutateAsync('yakureki-id')
 * ```
 */
export const usePatchCopyDoneMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (yakurekiId: string) => {
      await patchCopyDone(yakurekiId)
    },
    onSuccess: (_, yakurekiId) => {
      // 患者一覧のキャッシュをinvalidate（コピー済み表示更新のため）
      queryClient.invalidateQueries({
        queryKey: queryKeys.patients.all,
      })
      // 該当薬歴のキャッシュもinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
    },
  })
}

// ========================================
// usePatchMarkDismissedMutation — 完了化 (status → DISMISSED)
// ========================================

export const usePatchMarkDismissedMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (yakurekiId: string) => {
      await patchMarkDismissed(yakurekiId)
    },
    onSuccess: (_, yakurekiId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
    },
  })
}

// ========================================
// usePatchUndoDismissedMutation — 完了取り消し (DISMISSED → COPIED 戻し)
// ========================================

export const usePatchUndoDismissedMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (yakurekiId: string) => {
      await patchUndoDismissed(yakurekiId)
    },
    onSuccess: (_, yakurekiId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patients.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
    },
  })
}

// ========================================
// useDetachNSIPSMutation
// ========================================

/**
 * NSIPS連携解除Mutation（TanStack Query）
 *
 * 責務:
 * - NSIPS連携の解除
 * - 成功時に薬歴キャッシュをinvalidate
 *
 * 使用例:
 * ```tsx
 * const detachMutation = useDetachNSIPSMutation()
 * await detachMutation.mutateAsync('yakureki-id')
 * ```
 */
export const useDetachNSIPSMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (yakurekiId: string) => {
      await detachNSIPS(yakurekiId)
    },
    onSuccess: (_, yakurekiId) => {
      // 該当薬歴のキャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
      // 患者一覧のキャッシュもinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.patients.all,
      })
      // latest sidebar (薬歴リスト) も即時反映する (= 録音ライフサイクルと同じ prefix を叩く)
      queryClient.invalidateQueries({
        queryKey: ['yakurekiList'],
      })
    },
  })
}

// ========================================
// useSessionTypeMutation — 来局/在宅 (session_type) 切替
// ========================================

interface SessionTypeVariables {
  yakurekiId: string
  sessionType: SessionType
}

/**
 * 来局/在宅 (session_type) 切替Mutation（TanStack Query）
 *
 * 責務:
 * - 録音前 idle の event に session_type を設定 (back は PENDING の間のみ許可、以外は 409)
 * - 成功時に薬歴 detail をinvalidate (= event.session_type / 訪問報告書サイドカーの再取得)
 *
 * 使用例:
 * ```tsx
 * const setSessionType = useSessionTypeMutation()
 * setSessionType.mutate({ yakurekiId: 'xxx', sessionType: 'home_visit' })
 * ```
 */
export const useSessionTypeMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ yakurekiId, sessionType }: SessionTypeVariables) => {
      await patchSessionType(yakurekiId, sessionType)
    },
    onSuccess: (_, variables) => {
      // detail(id) = ['yakureki', id] は poll query ['yakureki', id, 'poll'] を prefix-match する
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(variables.yakurekiId),
      })
    },
  })
}

// ========================================
// useManualSoapMutation
// ========================================

/**
 * 録音スキップ Mutation（TanStack Query）
 *
 * 責務:
 * - 録音を経ずに空の SOAP を起こし、そのまま SOAP 編集画面へ進めるようにする
 *   (back は PENDING かつ SOAP 未作成のときのみ許可。以外は 409)
 * - 成功時に薬歴 detail と一覧を invalidate
 *
 * event.status が 'completed' になれば deriveViewStatus が engine を見ずに
 * L3 status='completed' を返すため、詳細画面は Post へ自動で切り替わる
 * (= 画面側の分岐追加は不要)。ただし poll query の refetchInterval は
 * status='processing' の間しか回らないので、ここで invalidate しないと
 * 古い event のまま画面が変わらない。
 *
 * 使用例:
 * ```tsx
 * const manualSoap = useManualSoapMutation()
 * await manualSoap.mutateAsync({ yakurekiId: 'xxx' })
 * ```
 */
export const useManualSoapMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ yakurekiId }: { yakurekiId: string }) => {
      await patchManualSoap(yakurekiId)
    },
    onSuccess: (_, variables) => {
      // detail(id) = ['yakureki', id] は poll query ['yakureki', id, 'poll'] を prefix-match する
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(variables.yakurekiId),
      })
      // 指導フェーズ (pre → post) が変わるのでサイドバー一覧も取り直す
      queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
    },
  })
}

// ========================================
// useGenerateFromMemoMutation
// ========================================

/**
 * メモから SOAP 生成 Mutation（TanStack Query）
 *
 * 責務:
 * - 録音を経ず、薬剤師メモだけを根拠に SOAP を生成させる
 *   (back は PENDING・SOAP 未作成・メモ 1 件以上のときのみ許可。以外は 409/400)
 * - 成功時に薬歴 detail と一覧を invalidate
 *
 * useManualSoapMutation と違い着地は 'processing' で、完了は polling が拾う。
 * その polling (refetchInterval) は status='processing' の間しか回らないため、
 * ここで invalidate して「まず processing を観測させる」ことが起動条件になる。
 */
export const useGenerateFromMemoMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ yakurekiId }: { yakurekiId: string }) => {
      await patchGenerateFromMemo(yakurekiId)
    },
    onSuccess: (_, variables) => {
      // detail(id) = ['yakureki', id] は poll query ['yakureki', id, 'poll'] を prefix-match する
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(variables.yakurekiId),
      })
      queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
    },
  })
}
